import Export from "@/components/Export";
import { ASSIGNED } from "@/constants/area";
import { ENGINEER, PROJECT_ADMIN, SUBCONTRACTOR, SUPER_ADMIN } from "@/constants/roles";
import {
  ACCEPTED,
  AREA_RELEASED,
  OVERDUE,
  PARTIALLY_ACCEPTED,
  REASSIGNED,
  REJECTED,
  REJECTION_ACCEPTED,
  RELEASED,
  RELEASE_APPROVED,
  RELEASE_REJECTED,
} from "@/constants/tasks";
import { useTasksByRequest } from "@/hooks/useTasks";
import { IRequest } from "@/interfaces/request.interface";
import { ITaskAction } from "@/interfaces/task.interface";
import { RootState } from "@/store/store";
import { convertDateString, isAuthorize } from "@/utils/common";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridToolbar,
} from "@progress/kendo-react-grid";
import React from "react";
import { useSelector } from "react-redux";
import { Info } from "lucide-react";

function AssignmentsGrid({
  setIsViewMode,
  readOnlyMode,
  handleAction,
  request,
}: {
  setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  readOnlyMode: boolean;
  handleAction: (data: ITaskAction) => void;
  request?: IRequest;
}) {
  const { user, userGroups } = useSelector((state: RootState) => state.user);
  const _exporter = React.useRef<ExcelExport>(null);

  const { data: response, refetch } = useTasksByRequest(request?.id!, {
    enabled: !!request?.id,
  });

  const isAdmin =
    user?.roles.includes(SUPER_ADMIN) || user?.roles.includes(PROJECT_ADMIN);

  const Status = (props: GridCellProps) => {
    let textColor = "";

    console.log("props.dataItem.status", props.dataItem.status);
    switch (props.dataItem.status) {
      case ASSIGNED:
      case REASSIGNED:
        textColor = "text-[#FF9800]";
        break;
      case OVERDUE:
        textColor = "text-[#D32F2F]";
        break;
      case RELEASED:
      case AREA_RELEASED:
      case RELEASE_APPROVED:
        textColor = "text-[#219653]";
        break;
      case PARTIALLY_ACCEPTED:
      case ACCEPTED:
        textColor = "text-[#0095ff]";
        break;
      case RELEASE_REJECTED:
      case REJECTED:
      case REJECTION_ACCEPTED:
        textColor = "text-[#EB5757]";
        break;
    }

    return (
      <td
        style={{ fontWeight: "700" }}
        className={`${textColor} uppercase font-black`}
      >
        {props.dataItem[props.field as string]}
      </td>
    );
  };

  
  const AssignedUsers = (props: GridCellProps) => {
    let assignees = "";
    let showGroupTooltip = false;
    if(props.dataItem.isUserGroupRequest) {
      const notActioned = props.dataItem?.users?.filter((x: { isLinked: boolean }) => !x.isLinked);
      if(notActioned.length === 0 && props.dataItem?.users?.length > 1) {
        assignees = `Users of Group: ${props.dataItem?.groupName}`
        showGroupTooltip = true;
      } else {
        assignees = props.dataItem?.users?.map((x: { id: string, name: string }) => x.name).join(", ");
      }
    } else {
      assignees = props.dataItem.assignedToDetails?.name;
    }


    return (
      <td style={{ overflow: 'visible' }}>
        <div className="flex items-center gap-2">
          {assignees}
          {showGroupTooltip && (
            <div className="relative group">
              <Info 
                className="text-blue-500 cursor-help hover:text-blue-600 transition-colors" 
                size={18}
              />
              <div 
                className="fixed hidden group-hover:block w-64 transform -translate-x-1/2"
                style={{ zIndex: 99999 }}
              >
                <div className="bg-gray-800 text-white text-sm rounded-lg shadow-xl p-3 mt-2">
                  <div className="font-semibold mb-2">Users in this group:</div>
                  <ul className="list-disc pl-4 space-y-1">
                    {props.dataItem?.users?.map((user: { id: string; name: string }) => (
                      <li key={user.id}>{user.name}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </td>
    );
  };

  const CreatedOn = (props: GridCellProps) => {
    return <td>{convertDateString(props.dataItem[props.field as string])}</td>;
  };

  const isPresentInGroup = (users: any[]) => {
    if(!isAuthorize([SUBCONTRACTOR, PROJECT_ADMIN])) return;
    return users.some((user) => user.id === user?.id && user.isLinked);
  }

  const isEngineerPresentInGroup = (dataItem: any) => {
    if (!isAuthorize([ENGINEER]) || !user?.id || !dataItem.groupId) return false;
    return userGroups.some((group) => group.id === dataItem.groupId && group.engineers?.includes(user.id));
  }

  const ActionCell = (props: GridCellProps) => {
    if (readOnlyMode) {
      return <td></td>;
    }

    switch (props.dataItem.status) {
      case ASSIGNED:
      case OVERDUE:
        if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() =>
                  handleAction({
                    ...props.dataItem,
                    action: "accept",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  })
                }
              >
                Process
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;
      case ACCEPTED:
      case PARTIALLY_ACCEPTED:
        if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() =>
                  handleAction({
                    ...props.dataItem,
                    action: "release",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  })
                }
              >
                Release
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }

        return <td></td>;

      case RELEASED:
        if (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) {
          return (
            <td className="space-x-2">
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() =>
                  handleAction({
                    ...props.dataItem,
                    action: "action",
                    reason: "",
                    requestWirNumber: request?.wirNumber,
                  })
                }
              >
                Process
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;

      case RELEASE_APPROVED:
        if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;

      case RELEASE_REJECTED:
        if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() =>
                  handleAction({
                    ...props.dataItem,
                    action: "release",
                    comments: "",
                    requestWirNumber: request?.wirNumber,
                    isResubmit: true,
                  })
                }
              >
                Resubmit
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;
      case REJECTED:
        if (
          (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) &&
          request?.status !== RELEASED
        ) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;
      case REASSIGNED:
      case OVERDUE:
        if (isAdmin || props.dataItem.userId === user?.id || isPresentInGroup(props.dataItem.users)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() =>
                  handleAction({
                    ...props.dataItem,
                    action: "accept",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  })
                }
              >
                Process
              </button>
            </td>
          );
        } else if (isAdmin || props.dataItem.createdBy === user?.id || isEngineerPresentInGroup(props.dataItem)) {
          return (
            <td>
              <button
                className="text-primary font-bold bg-white border border-primary px-2 py-[6px]"
                onClick={() => {
                  handleAction({
                    ...props.dataItem,
                    action: "view",
                    requestWirNumber: request?.wirNumber,
                    comments: "",
                  });
                  setIsViewMode(true);
                }}
              >
                View
              </button>
            </td>
          );
        }
        return <td></td>;
      default:
        return <td></td>;
    }
  };

  const Activities = (props: GridCellProps) => {
    return (
      <td className="overflow-hidden text-ellipsis">
        {props.dataItem.activities?.map((activity: any) => (
          <p key={activity.name} className="text-txtPrimary">
            {activity.name}
          </p>
        ))}
      </td>
    );
  };

  const excelExport = () => {
    if (_exporter.current) {
      if (response?.data && response.data.length > 0) {
        // Get the formatted data
        const exportData = getExportData();

        // Save with the formatted data
        _exporter.current.save(exportData);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            const exportData = getExportData();
            _exporter.current?.save(exportData);
          }, 300);
        });
      }
    }
  };

  const getExportData = () => {
    if (!response?.data) return [];

    return response.data.map((item) => {
      return {
        ...item,
        // Format activities as a comma-separated string of activity names
        activities: Array.isArray(item.activities)
          ? item.activities.map((a: any) => a.name).join(", ")
          : item.activities || "",
        createdOn: item.createdOn
          ? new Date(item.createdOn).toLocaleDateString()
          : "",
      };
    });
  };

  return (
    <Grid
      style={{ width: "100%", maxWidth: "100%" }}
      data={response?.data}
      className="overflow-x-hidden"
    >
      <GridToolbar className="py-1">
        <div className="text-[16px] font-medium text-txtPrimary flex">
          <img src="./images/assignments-icon.png" alt="" className="mr-2" />
          <p>Assignments</p>
        </div>
        {isAuthorize([SUPER_ADMIN, PROJECT_ADMIN, ENGINEER]) && (
          <div className="cursor-pointer ml-auto" onClick={excelExport}>
            <Export />
            <ExcelExport
              data={response?.data || []}
              collapsible={true}
              fileName="Assignments.xlsx"
              ref={_exporter}
            >
              <ExcelExportColumn
                field="createdByDetails.name"
                title="Assigned By"
                width={200}
              />
              <ExcelExportColumn
                field="companyDetails.name"
                title="Company Name"
                width={180}
              />
              <ExcelExportColumn
                field="assignedToDetails.name"
                title="User"
                width={220}
              />
              <ExcelExportColumn
                field="activities"
                title="Activities"
                width={200}
              />
              <ExcelExportColumn field="status" title="Status" width={200} />
              <ExcelExportColumn
                field="createdOn"
                title="Created On"
                width={200}
              />
            </ExcelExport>
          </div>
        )}
      </GridToolbar>
      <GridColumn field="createdByDetails.name" title="Assigned By" />
      <GridColumn field="companyDetails.name" title="Company" width={180}/>
      <GridColumn field="activities" cells={{ data: AssignedUsers }} title="User" width={200} />
      <GridColumn
        field="activities"
        title="Activities"
        cells={{
          data: Activities,
        }}
      />
      <GridColumn
        field="status"
        title="Status"
        cells={{
          data: Status,
        }}
        width={125}
      />
      <GridColumn
        field="createdOn"
        title="Assigned On"
        cells={{
          data: CreatedOn,
        }}
        width={175}
      />
      <GridColumn
        width={100}
        field="status"
        title="Action"
        cells={{
          data: ActionCell!,
        }}
      />
    </Grid>
  );
}

export default AssignmentsGrid;
